import type { OrderInfo } from '@@partner:types:app'
import type { RefundPositionInfo } from '../RefundStateContext'

import React, { useState } from 'react'
import { formatPrice } from '@@partner:helpers/util'

import {
  Checkbox,
  Form,
  Tag,
  RadioGroup,
  Radio,
  Message,
  Divider,
  Toggle
} from 'rsuite'
import {
  useLazyFetchCalculateRefund,
  useFetchCalculateRefund
} from '../../../../graphql/query/order/calculateRefund.gql'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import AppLoader from '@components/common/AppLoader'

export interface RefundConfig {
  fullRefund: boolean
  refundMethod: string
  returnToShop: boolean
}

export interface RefundPositionsContentProps {
  order: OrderInfo
  refundPositions: RefundPositionInfo[]
  refundConfig: RefundConfig
  onConfigChange: (newCfg: RefundConfig) => void
}

const RefundProductsContent: React.FC<RefundPositionsContentProps> = ({
  order,
  refundPositions = [],
  refundConfig,
  onConfigChange
}) => {
  const [ config, setConfig ] = useState<RefundConfig>(refundConfig)

  const { data, loading, error } = useFetchCalculateRefund({
    refundInfo: {
      orderId: order.id,
      positions: refundPositions.map((item) => ({
        amount: item.refundAmount,
        id: item.id
      })),
      ...config
    }
  })

  const handleConfigChange = (newState: RefundConfig) => {
    setConfig(newState)
    onConfigChange(newState)
  }

  if (error) {
    return (
      <GraphQLCommonErrorDisplay
        title="Возникла ошибка во время рассчёта суммы возврата"
        error={error}
      />
    )
  }

  const { total = 0, deliveryTotal = 0 } = data?.refundInfo ?? {}
  const sumToRefund = total + deliveryTotal

  return (
    <div className="RefundProducts">
      <div className="my-2">
        {loading && <AppLoader.Inner content="Расчёт суммы возврата" />}

        <label className="font-bold mb-2 block">
          Способ осуществления возврата
        </label>
        <RadioGroup
          onChange={(selectedMethod) => {
            handleConfigChange({
              ...config,
              refundMethod: selectedMethod.toString()
            })
          }}
          value={config.refundMethod}
        >
          <Radio value="money">Деньгами</Radio>
          <Radio value="bonuses">Бонусами на карту</Radio>
        </RadioGroup>
      </div>

      <div className="my-2">
        <span className="font-bold mb-2 block">
          Товар возвращается в магазин?
        </span>
        <RadioGroup
          onChange={(selected) => {
            handleConfigChange({
              ...config,
              returnToShop: selected === 1
            })
          }}
          value={config.returnToShop === true ? 1 : 0}
        >
          <Radio value={1}>Да, товар будет возвращен в магазин</Radio>
          <Radio value={0}>Нет, товар не будет возвращен в магазин</Radio>
        </RadioGroup>
      </div>

      <div className="my-2">
        <span className="font-bold mb-2 block">Полный возврат</span>
        <Toggle
          onChange={(checked) => {
            handleConfigChange({
              ...config,
              fullRefund: checked
            })
          }}
          checked={config.fullRefund}
        >
          Полный возврат
        </Toggle>
      </div>

      <Message
        type="info"
        showIcon
        header={<strong>Информация о возврате</strong>}
      >
        <div>
          <p>Вы собираетесь оформить возврат для заказа {order.id}</p>
          <p>Количество позиций {refundPositions.length}</p>
          <p>
            <strong>Сумма возврата</strong>
          </p>
          <p>
            {' '}
            <Tag color="violet">{formatPrice(sumToRefund)} </Tag>
            <small>
              (товары: {formatPrice(total)}, доставка:{' '}
              {formatPrice(deliveryTotal)})
            </small>
          </p>
        </div>
      </Message>
    </div>
  )
}

export default RefundProductsContent
